/**
 * src/views/focus/FocusBoard.jsx — Kanban board for Focus tasks
 * AC Nautitech Executive Assistant
 *
 * Columns: To Do | In Progress | Blocked | Done
 * Drag-and-drop between columns to change task status.
 * Uses @dnd-kit for touch + mouse support.
 */

import { useState, useMemo, useCallback } from 'react';
import { Circle, Clock, AlertOctagon, CheckCircle2 } from 'lucide-react';
import { AnimatePresence } from 'framer-motion';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import TaskCard from '../../components/shared/TaskCard';
import SortableTaskRow from './SortableTaskRow';
import { useT } from '../../lib/i18n/I18nContext';

// Column keys are canonical task_status slugs (matches normalizeTaskStatus()
// and FocusView.jsx line 78 which sets task.st = normalizeTaskStatus(record.status)).
// Display labels come from i18n via labelKey. Fixed 2026-04-08: previously
// the column keys were display labels ('To Do', 'In Progress', ...) which
// mismatched the slug form on task.st, so grouped[st] was always undefined,
// the fallback dumped every task into 'To Do', and drag visually did nothing
// even though the DB write succeeded.
const COLUMNS = [
  { key: 'todo',        labelKey: 'status.todo',        color: 'var(--stodo, #94a3b8)', icon: Circle },
  { key: 'in_progress', labelKey: 'status.inProgress',  color: 'var(--spro, #3b82f6)',  icon: Clock },
  { key: 'blocked',     labelKey: 'status.blocked',     color: 'var(--warn, #d97706)',  icon: AlertOctagon },
  { key: 'done',        labelKey: 'status.done',        color: 'var(--sdn, #22c55e)',   icon: CheckCircle2 },
];

/* ── Droppable column ─────────────────────────────────
 * Each column is BOTH a droppable (for cross-column status change) AND a
 * SortableContext (for within-column reorder). dnd-kit handles the overlap
 * natively: over.id resolves to a task id for within-column drops and to
 * the column key for empty-column / header drops. See handleDragEnd below.
 *
 * isOverColumn: computed by the parent from onDragOver — tracks whether the
 * pointer is currently over this column REGARDLESS of whether the immediate
 * over target is a sortable card inside it. Without this, dragging over a
 * non-empty column only sets useDroppable.isOver=true for empty columns
 * (because the sortable child wins), so the dashed-border feedback never
 * showed on columns that had tasks. Fix 2026-04-08.
 */
function DroppableColumn({ col, items, t, onSelectTask, onCycleStatus, activeId, isOverColumn }) {
  const { isOver, setNodeRef } = useDroppable({ id: col.key });
  const itemIds = useMemo(() => items.map(tk => tk.id), [items]);
  const showDropHighlight = isOver || isOverColumn;

  return (
    <div
      ref={setNodeRef}
      aria-label={`${t(col.labelKey)} column`}
      className={`rounded-xl flex flex-col min-h-0 transition-all duration-150 ${
        showDropHighlight
          ? 'border-2 border-dashed border-accent bg-accent-bg'
          : 'border border-bdr bg-glass'
      }`}
    >
      {/* Column header */}
      <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--bdr, #E8ECF2)', display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: col.color }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--t1, #1B2B4B)' }}>
          {t(col.labelKey)}
        </span>
        <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 500, background: 'var(--card, #fff)', borderRadius: 8, padding: '2px 8px', border: '1px solid var(--bdr, #E8ECF2)', color: 'var(--tm, #8896AB)' }}>
          {items.length}
        </span>
      </div>

      {/* Cards — wrapped in SortableContext for within-column reorder */}
      <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 8, overflowY: 'auto', flex: 1, overscrollBehavior: 'none' }}>
        <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
          {items.map((task, i) => (
            <SortableTaskRow
              key={task.id}
              task={task}
              index={i}
              activeId={activeId}
              onClick={() => onSelectTask?.(task)}
              onCycleStatus={() => onCycleStatus?.(task.id)}
              variant="focus"
            />
          ))}
        </SortableContext>
        {items.length === 0 && (
          <div className="p-6 text-center text-sm text-tm opacity-60">
            {t('board.noTasks')}
          </div>
        )}
      </div>
    </div>
  );
}

export default function FocusBoard({ tasks, onStatusChange, onReorder, onSelectTask, onCycleStatus }) {
  const { t } = useT();
  const [activeId, setActiveId] = useState(null);
  const [overColumn, setOverColumn] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    // Mobile: longer delay + tight tolerance so scrolling through a dense
    // column doesn't accidentally trigger a drag. Previous attempt at
    // within-column sort failed on mobile due to scroll/drag conflicts;
    // bumping delay from 300 -> 350 and forcing touchAction:none on the
    // grip (via SortableTaskRow) should fix that.
    useSensor(TouchSensor, { activationConstraint: { delay: 350, tolerance: 8 } }),
  );

  const grouped = useMemo(() => {
    const g = { todo: [], in_progress: [], blocked: [], done: [] };
    for (const task of tasks) {
      const st = task.st || 'todo';
      if (g[st]) g[st].push(task);
      else g.todo.push(task);
    }
    return g;
  }, [tasks]);

  const activeTask = activeId ? tasks.find(tk => tk.id === activeId) : null;

  const handleDragStart = useCallback((event) => {
    setActiveId(event.active.id);
    setOverColumn(null);
  }, []);

  const VALID_STATUSES = useMemo(() => new Set(COLUMNS.map(c => c.key)), []);

  // Given a task id, find which column slug it belongs to based on current
  // grouping. Used to distinguish cross-column drops from within-column
  // reorders when over.id is another task id (sortable case).
  const columnOfTask = useCallback((taskId) => {
    for (const colKey of Object.keys(grouped)) {
      if (grouped[colKey].some(tk => tk.id === taskId)) return colKey;
    }
    return null;
  }, [grouped]);

  // onDragOver fires on every pointer move during a drag. over.id may be
  // either a column key or a sortable card id; either way we resolve it
  // to the column slug and light up that column's dashed border.
  const handleDragOver = useCallback((event) => {
    const { over } = event;
    if (!over) { setOverColumn(null); return; }
    const next = VALID_STATUSES.has(over.id) ? over.id : columnOfTask(over.id);
    setOverColumn(next || null);
  }, [VALID_STATUSES, columnOfTask]);

  const handleDragEnd = useCallback((event) => {
    setActiveId(null);
    setOverColumn(null);
    const { active, over } = event;
    if (!over) return;
    const activeTaskId = active.id;
    if (activeTaskId === over.id) return; // dropped on itself

    const sourceCol = columnOfTask(activeTaskId);
    if (!sourceCol) return;

    // over.id can be either a column key (dropped on empty column / column
    // header area) or a task id (dropped on another card inside a column).
    const droppedOnColumn = VALID_STATUSES.has(over.id);
    const targetCol = droppedOnColumn ? over.id : columnOfTask(over.id);
    if (!targetCol) return;

    if (targetCol !== sourceCol) {
      // Cross-column drop → status change. Persistence handled upstream.
      onStatusChange(activeTaskId, targetCol);
      return;
    }

    // Within-column reorder.
    if (droppedOnColumn) return; // dropped on column header with no card target
    const items = grouped[sourceCol];
    const oldIndex = items.findIndex(tk => tk.id === activeTaskId);
    const newIndex = items.findIndex(tk => tk.id === over.id);
    if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return;
    const reordered = arrayMove(items, oldIndex, newIndex);
    if (onReorder) onReorder(reordered.map(tk => tk.id));
  }, [grouped, columnOfTask, VALID_STATUSES, onStatusChange, onReorder]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={() => { setActiveId(null); setOverColumn(null); }}
    >
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 min-h-[400px]">
        {COLUMNS.map(col => (
          <DroppableColumn
            key={col.key}
            col={col}
            items={grouped[col.key] || []}
            t={t}
            activeId={activeId}
            isOverColumn={overColumn === col.key}
            onSelectTask={onSelectTask}
            onCycleStatus={onCycleStatus}
          />
        ))}
      </div>

      {/* Drag overlay — renders the card being dragged */}
      <DragOverlay dropAnimation={null}>
        {activeTask ? (
          <div style={{ opacity: 0.9, transform: 'rotate(2deg)', pointerEvents: 'none' }}>
            <TaskCard task={activeTask} variant="focus" />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
